import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ExtendedContact } from '../services/contact.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ContactService } from '../services/contact.service';
import { PresenceService } from '../services/presence.service';
import { Subscription } from 'rxjs';
import { PresenceState } from '../enums/presence-state.enum';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPhone, faPhoneVolume } from '@fortawesome/free-solid-svg-icons';
import { SipService } from '../services/sip.service';


@Component({
  selector: 'app-contact-card',
  templateUrl: './contact-card.component.html',
  styleUrls: ['./contact-card.component.css'],
  providers: [ContactService],
})
export class ContactCardComponent implements OnInit, OnDestroy {
  @Input() contact!: ExtendedContact;

  @Output() call = new EventEmitter<ExtendedContact>();
  @Output() delete = new EventEmitter<ExtendedContact>();

  private subscriptions = new Subscription();
  private dialogMap = new Map<number, { dialogId: number; state: string }>();

  constructor(
    private contactService: ContactService,
    private presenceService: PresenceService,
    public sipService: SipService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    if (!this.contact.eveUserName) {
      this.contact.presenceState = PresenceState.Unknown;
      return;
    }
    this.subscriptions.add(this.loadAvatar());
    this.subscriptions.add(this.subscribeToPresence());
  }

  private loadAvatar(): Subscription {
    return this.contactService.getAvatar(this.contact.eveUserName!).subscribe({
      next: (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        this.contact.avatarUrl = this.sanitizer.bypassSecurityTrustUrl(
          objectUrl
        ) as string;
      },
      error: () => {
        this.contact.avatarUrl = 'assets/eveuser-avatar.png';
      },
      
    });
  }

  private subscribeToPresence(): Subscription {
    return this.presenceService.subscribe(this.contact.eveUserName!).subscribe({
      next: (presenceData) => {
        const presence = presenceData.data?.presence;
        if (!presence) {
          this.contact.presenceState = PresenceState.Unknown;
          return;
        }

        if (presence.__typename === 'DialogPresence') {
          const dialogs = presence.dialogs || [];

          // Full replaces, incremental updates otherwise
          if (presence.state === 'full') {
            this.dialogMap.clear();
            dialogs.forEach((d) => {
              if (d.state !== 'terminated') {
                this.dialogMap.set(d.dialogId, d);
              }
            });
          } else {
            dialogs.forEach((d) => {
              if (d.state === 'terminated') {
                this.dialogMap.delete(d.dialogId);
              } else {
                this.dialogMap.set(d.dialogId, d);
              }
            });
          }

          const activeStates = [...this.dialogMap.values()].map((d) => d.state);
          if (activeStates.includes('confirmed')) {
            this.contact.presenceState = PresenceState.Confirmed;
          } else if (activeStates.includes('early')) {
            this.contact.presenceState = PresenceState.Early;
          } else {
            this.contact.presenceState = PresenceState.Available;
          }
        } else if (presence.__typename === 'StatusPresence') {
          const status = (presence.status || '').toLowerCase();
          if (status === 'available') {
            this.contact.presenceState = PresenceState.Available;
          } else if (status === '') {
            this.contact.presenceState = PresenceState.Unknown;
          } else {
            this.contact.presenceState = PresenceState.Unavailable;
          }
        }
      },
      error: (err) => {
        console.warn(`❌ Presence error for ${this.contact.eveUserName}`, err);
        this.contact.presenceState = PresenceState.Unknown;
      },
    });
  }

  onDelete() {
    this.delete.emit(this.contact);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getPresenceClass(): string {
    switch (this.contact.presenceState) {
      case PresenceState.Confirmed:
        return 'presence-red';
      case PresenceState.Early:
        return 'presence-yellow';
      case PresenceState.Available:
        return 'presence-green';
      case PresenceState.Unavailable:
        return 'presence-red';
      case PresenceState.Unknown:
      default:
        return 'presence-none';
    }
  }

  getPresenceIcon(): IconDefinition | null {
    switch (this.contact.presenceState) {
      case PresenceState.Confirmed:
        return faPhone;
      case PresenceState.Early:
        return faPhoneVolume;
      default:
        return null;
    }
  }

  showPresenceIcon(): boolean {
    return [PresenceState.Confirmed, PresenceState.Early].includes(
      this.contact.presenceState as PresenceState
    );
  }

  getAvatarUrl(): string {

    return this.contact.avatarUrl || 'assets/eveuser-avatar.png';
  }
}
